import pastorRuanPhoto from "@/assets/igrejas/pessoas/pr-ruan-magalhaes-comunidade-crista-zelo.webp";
import pastorLeonardoBastosPhoto from "@/assets/igrejas/pessoas/pr-leonardo-bastos-ministerio-decisao.webp";
import WhatsAppCTA from "./WhatsAppCTA";
import { RatingPanel } from "./reviews/rating-panel";
import { useSectionViewGoal } from "@/hooks/useSectionViewGoal";
import { SectionHeading } from "./primitives";
import { CONTAINER, SECTION_Y } from "./ui-tokens";
import { cn } from "@/lib/utils";

// Luiz Felipe, a Pastora Carina e a Hetiny já aparecem em case-ieq.tsx —
// não duplicar aqui. Só entra fala que veio pelo /avaliar e foi aprovada para o
// site (decisão do Vinicius, 03/11/2026); quem não tem foto aparece com as
// iniciais.
//
// O primeiro da lista é o destaque (`const [featured, ...rest]`): a ordem aqui
// é que decide. O lugar era do Pr. Régis, do Cenáculo da Fé, que deixou de ser
// cliente — depoimento de quem saiu não fica publicado. Todo depoimento desta
// lista precisa ser de uma igreja que ainda está no marquee do `trusted-by`.
//
// Depoimento é fala publicada, palavra por palavra: não edite o `content`.
const testimonials: {
  content: string;
  author: string;
  role: string;
  image?: string;
}[] = [
  {
    content:
      "Há algum tempo estávamos pesquisando uma plataforma que nos ajudasse organizar departamentos, líderes, treinamento de forma rápida e eficaz, e dentre várias oferecendo até o que não é necessário para igrejas, aparece a Ecclesiato e foi uma boa surpresa, pois tem nos atendido de forma excelente e com muita eficácia. Recomendo e indico as igrejas que estão procurando organização e fluidez no serviço!",
    author: "Pr. Ruan Magalhães",
    role: "Comunidade Cristã Zelo",
    image: pastorRuanPhoto as string,
  },
  {
    content:
      "São tantas funcionalidades que é difícil escolher por onde começar! As que mais agregaram ao nosso dia a dia foram a gestão de pessoas, com controle de membros, frequentadores e visitantes, além de recursos que facilitam o acompanhamento e a criação de estratégias para que eles retornem; e a gestão financeira, com controle de contas, lançamentos futuros e relatórios simples e intuitivos.",
    author: "Pr. Leonardo Bastos",
    role: "Igreja Ministério Decisão",
    image: pastorLeonardoBastosPhoto as string,
  },
  {
    content:
      "Vale muito a pena! Conseguimos visualizar o fechamento de forma muito mais rápida e prática, além de facilitar bastante a organização das escalas e da cantina. Tudo fica mais centralizado e fácil de acompanhar.",
    author: "Bruna Bissi",
    role: "Liderança de jovens · IEQ Central de Piracicaba",
  },
];

/** "Bruna Bissi" → "BB", para quem não tem foto. */
function iniciais(nome: string) {
  return nome
    .split(/s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0].toUpperCase())
    .join("");
}

function Quote({
  t,
  featured,
}: {
  t: (typeof testimonials)[number];
  featured?: boolean;
}) {
  return (
    <figure
      className={cn(
        "lp-reveal lp-surface flex flex-col justify-between gap-8 p-6 md:p-8",
        featured && "md:col-span-2 md:p-10",
      )}
    >
      <blockquote
        className={cn(
          "leading-relaxed text-pretty text-foreground",
          featured
            ? "max-w-4xl text-xl font-medium tracking-tight md:text-[1.75rem] md:leading-snug"
            : "text-base",
        )}
      >
        “{t.content}”
      </blockquote>
      <figcaption className="flex items-center gap-3">
        {t.image ? (
          <img
            src={t.image}
            alt=""
            className={cn(
              "lp-img shrink-0 rounded-full object-cover",
              featured ? "size-12" : "size-10",
            )}
            width={featured ? 48 : 40}
            height={featured ? 48 : 40}
            loading="lazy"
          />
        ) : (
          <span
            aria-hidden
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-muted-foreground",
              featured ? "size-12" : "size-10",
            )}
          >
            {iniciais(t.author)}
          </span>
        )}
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-foreground">
            {t.author}
          </span>
          <span className="block text-sm text-muted-foreground">{t.role}</span>
        </span>
      </figcaption>
    </figure>
  );
}

export default function Testimonials() {
  const ref = useSectionViewGoal("testimonials");
  const [featured, ...rest] = testimonials;

  return (
    <section
      ref={ref}
      id="depoimentos"
      aria-labelledby="depoimentos-titulo"
      className={SECTION_Y}
    >
      <div className={CONTAINER}>
        <SectionHeading
          id="depoimentos-titulo"
          title="Quem trocou a planilha pelo Ecclesiato"
          muted="não volta atrás."
        />

        {/* Os depoimentos são curados por nós; a nota agregada é o contrário
            — média de todo mundo que avaliou, com as de uma estrela
            acessíveis no modal. Os dois juntos é que convencem. Some sozinha
            abaixo de três avaliações. */}
        <RatingPanel className="mt-12" />

        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Quote t={featured} featured />
          {rest.map((t) => (
            <Quote key={t.author} t={t} />
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-base font-medium text-foreground">
            Quer ver o Ecclesiato funcionando na rotina da sua igreja?
          </p>
          <WhatsAppCTA
            variant="outline"
            text="Pedir uma demonstração"
            customMessage="Olá! Vi os depoimentos de pastores e gostaria de ver uma demonstração do Ecclesiato para a minha igreja."
          />
        </div>
      </div>
    </section>
  );
}
